import { OpenInNewOutlined, VideocamOutlined } from '@mui/icons-material';
import { Alert, AlertTitle, Box, Button, Paper, Skeleton, SvgIcon, Typography } from '@mui/material';
import Utils from '@utils/index';
import React from 'react';

type EventContentUniFiVideoProps = {
  label: string;
  uniFiProtectUrl?: string;
  thumbnailUri?: string;
}

const EventContentUniFiVideo: React.FC<EventContentUniFiVideoProps> = ({ label, uniFiProtectUrl, thumbnailUri }) => {
  const [image, setImage] = React.useState<string>();
  const [imageLoaded, setImageLoaded] = React.useState<boolean>(false);
  const [imageError, setImageError] = React.useState<boolean>(false);

  React.useEffect(() => {
    if (thumbnailUri) {
      try {
        const { key, bucket } = Utils.S3.parseS3Uri(thumbnailUri);
        Utils.S3.getImageUrl(key, {}, bucket)
          .then((response) => setImage(response.url))
          .catch(() => setImageError(true))
      } catch {
        setImageError(true)
      }
    }
  }, [thumbnailUri]);

  const noImage = !thumbnailUri || imageError;

  if (noImage) {
    return (
      <>
        <Paper variant="outlined" sx={{ display: "flex", justifyContent: "center", alignItems: "center" }}>
          <Box maxWidth="386px" display="flex" flexDirection="column" gap="2rem">
            <Box display="flex" flexDirection="column" alignItems="center">
              <SvgIcon color="primary" sx={{ fontSize: 35 }}>
                <VideocamOutlined />
              </SvgIcon>
              <Typography variant="subtitle1" align="center" fontWeight={700}>Video available in UniFi Protect</Typography>
              <Typography variant="body1" align="center">This alert is from UniFi Protect. Open the UniFi Protect app to view.</Typography>
            </Box>
            {uniFiProtectUrl &&
              <Button variant="contained" fullWidth endIcon={<OpenInNewOutlined />} href={uniFiProtectUrl} target="_blank" rel="noreferrer">Open video in UniFi Protect</Button>
            }
          </Box>
        </Paper>
        <Alert severity="info">
          <AlertTitle>See Video in UniFi Protect</AlertTitle>
          This event was captured by a partner camera and is not available for viewing in the Forsight app.
        </Alert>
      </>
    )
  }

  return (
    <>
      <Paper variant="outlined" sx={{ display: "grid", gridTemplateRows: "min-content minmax(0, 1fr)", height: "100%" }}>
        <Box
          height="2.5rem"
          display="flex"
          alignItems="center"
          paddingX="1rem"
        >
          <Typography variant="subtitle1" style={{ whiteSpace: "nowrap" }}>{label}</Typography>
        </Box>
        {!imageLoaded &&
          <Skeleton animation="wave" variant="rectangular" width="100%" height="100%" />
        }
        <Box
          position="relative"
          bgcolor="black"
          display={imageLoaded ? "block" : "none"}
          overflow="hidden"
        >
          <Box position="absolute" top={0} left={0} right={0} bottom={0} display="flex" flexDirection="column">
            <Box flex="1" display="flex" alignItems="center" justifyContent="center" px="1rem">
              <Typography variant="body1" align="center" sx={{ color: "white" }}>
                This is a snapshot from the event video. Watch full video in{" "}
                {uniFiProtectUrl ? (
                  <a
                    href={uniFiProtectUrl}
                    target="_blank"
                    rel="noreferrer"
                    style={{ color: "white", textDecoration: "underline", display: "inline-flex", alignItems: "center", gap: "0.25rem", verticalAlign: "middle" }}
                  >
                    UniFi Protect
                    <OpenInNewOutlined fontSize="inherit" />
                  </a>
                ) : (
                  "UniFi Protect"
                )}
              </Typography>
            </Box>
            <img
              src={image}
              alt=""
              onLoad={() => setImageLoaded(true)}
              onError={() => setImageError(true)}
              style={{ width: "100%", height: "auto", minHeight: 0, maxHeight: "calc(100% - 2rem * 2)", objectFit: "contain", display: "block" }}
            />
            <Box flex="1" />
          </Box>
        </Box>
      </Paper>
      {imageLoaded &&
        <Alert severity="info" action={
          uniFiProtectUrl &&
            <Button
              variant="contained"
              endIcon={<OpenInNewOutlined />}
              href={uniFiProtectUrl}
              target="_blank"
              rel="noreferrer">
                Open video in UniFi Protect
            </Button>
        }>
          <AlertTitle>See Video in UniFi Protect</AlertTitle>
          Open in UniFi to view event video and details.
        </Alert>
      }
    </>
  )
}

export default EventContentUniFiVideo;